import { createEntityAdapter, createSelector, EntityState } from "@reduxjs/toolkit";

import { ACCOUNT_CONTEXT } from "fond/constants";
import {
  Account,
  AccountModule,
  BillingCycle,
  CreateAccountRequestBody,
  GetAccountUsersResponse,
  OkResponse,
  Optional,
  PaymentStatus,
  Store,
  Subscription,
  SubscriptionStatus,
  UpdateAccountRequestBody,
  Usage,
  UserAccountAllocationResponse,
} from "fond/types";
import { getItem } from "fond/utils/localStorage";

import { apiSlice } from "./apiSlice";
import { selectUserAccountIdAtRandom, selectUserId } from "./userSlice";

export type GetAccountSubscriptionUsageParams = {
  subscriptionId: string;
  type?: "planner" | "design";
  start_date?: string;
  end_date?: string;
};

export type GetAccountsResponse = {
  Items: Account[];
};

export const enum AccountLicenseStatus {
  VALID = "valid",
  EXPIRED = "expired",
}

export type GetAccountSubscriptionsResponse = {
  AccountSubscriptions: Optional<Subscription, "BillingCycles">[];
};

export const accountsAdapter = createEntityAdapter<Account>({
  selectId: (entity: Account): string => entity.ID,
});
const accountsInitialState = accountsAdapter.getInitialState();

export type GetAccountModulesResponse = {
  Modules: AccountModule[];
};

/**
 * Accounts API Slice
 */
export const accountSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    createAccount: build.mutation<Account, CreateAccountRequestBody>({
      query: (accountDetails) => ({
        url: `/v2/accounts`,
        method: "POST",
        body: accountDetails,
      }),
      invalidatesTags: [{ type: "Account", id: "LIST" }],
    }),
    getAccounts: build.query<EntityState<Account>, void>({
      query: () => `/v2/accounts`,
      transformResponse: (response: GetAccountsResponse) => accountsAdapter.setAll(accountsInitialState, response.Items),
      providesTags: (result) =>
        result ? [...result.ids.map((id) => ({ type: "Account" as const, id })), { type: "Account", id: "LIST" }] : [{ type: "Account", id: "LIST" }],
    }),
    updateAccount: build.mutation<Account, UpdateAccountRequestBody>({
      query: ({ ID, ...accountData }) => ({
        url: `/v2/accounts/${ID}`,
        method: "PUT",
        body: accountData,
      }),
      invalidatesTags: (result) => [
        { type: "Account" as const, id: result?.ID ?? "LIST" },
        // We are required to invalidate the Folders and Projects as well to ensure the Account object
        // within these entities is also updated.
        { type: "Folder", id: "LIST" },
        { type: "Project", id: "LIST" },
      ],
    }),
    transferPermissions: build.mutation<OkResponse, { accountId: string; sourceUserId: string; targetUserId: string }>({
      query: ({ accountId, sourceUserId, targetUserId }) => ({
        url: `/v2/accounts/${accountId}/transfer-permissions`,
        method: "POST",
        body: {
          SourceUserID: sourceUserId,
          TargetUserID: targetUserId,
        },
      }),
      invalidatesTags: [{ type: "Permission", id: "LIST" }],
    }),
    getAccountSubscriptions: build.query<Subscription[], string>({
      query: (accountId: string) => `/v2/accounts/${accountId}/account-subscriptions?order=desc`,
      transformResponse: (response: GetAccountSubscriptionsResponse) => {
        // We inject the Planner package information of the current active billing
        // cycle into the root Subscription object to avoid a lot of code changes.
        // FIXME - https://biarrinetworks.atlassian.net/browse/FND-3789.
        return response?.AccountSubscriptions.map((subscription) => {
          // handle old API schema (no 'BillingCycles' field)
          if (subscription?.BillingCycles === undefined) {
            // The PaymentStatus field is included in the type definition, but is not currently observed by any code.
            // Nevertheless, we calculate it here in an attempt to keep things consistent.
            let paymentStatus: PaymentStatus = "PENDING_PAYMENT";

            return {
              ...subscription,
              BillingCycles: [
                {
                  ID: "",
                  // Default to yearly as this was the only option in the old API
                  BillingPeriod: "yearly",
                  StartTime: subscription.StartTime,
                  EndTime: subscription.EndTime,
                  // This information isn't available in the old API schema
                  CreateTime: subscription.StartTime,
                  // This status doesn't match what the new API will return (the type definition of BillingCycle.Status
                  // isn't accurate). Fortunately, the code using this data only cares about whether the status
                  // is 'ACTIVE' or not.
                  Status: subscription.Status,
                  LicenseCount: subscription.LicenseCount,
                  Design: subscription.Design,
                  Planner: subscription.Planner,
                  PaymentStatus: paymentStatus,
                },
              ] as BillingCycle[],
            };
          }

          // handle new API schema
          const activeBillingCycle = subscription.BillingCycles.find(({ Status }) => Status === "ACTIVE");

          return {
            ...subscription,
            Design: activeBillingCycle?.Design || null,
            Planner: activeBillingCycle?.Planner || null,
            LicenseCount: activeBillingCycle?.LicenseCount || null,
          } as Subscription;
        });
      },
      providesTags: (result) =>
        result
          ? [...result.map((subscription) => ({ type: "Subscription" as const, id: subscription.ID })), { type: "Subscription", id: "LIST" }]
          : [{ type: "Subscription", id: "LIST" }],
    }),
    getAccountSubscriptionUsage: build.query<Usage, GetAccountSubscriptionUsageParams>({
      query: ({ subscriptionId, type, start_date, end_date }: GetAccountSubscriptionUsageParams) => {
        const params = new URLSearchParams();
        if (type) params.append("type", type);
        if (start_date) params.append("start_date", start_date);
        if (end_date) params.append("end_date", end_date);

        const queryString = params.toString();
        return `/v2/account-subscriptions/${subscriptionId}/usage${queryString ? `?${queryString}` : ""}`;
      },
    }),
    getAccountUsers: build.query<UserAccountAllocationResponse[], string>({
      query: (accountId: string) => `/v2/accounts/${accountId}/users`,
      transformResponse: (response: GetAccountUsersResponse) => {
        return response?.Allocations || [];
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map((allocation) => ({ type: "UserAccountAllocation" as const, id: allocation.ID }), {
                type: "UserAccountAllocation",
                id: "LIST",
              }),
            ]
          : [{ type: "UserAccountAllocation", id: "LIST" }],
    }),
    getAccountModules: build.query<GetAccountModulesResponse, string>({
      query: (accountId: string) => `/v2/accounts/${accountId}/modules`,
      providesTags: (result) =>
        result
          ? [...result.Modules.map(({ Name }) => ({ type: "AccountModules" as const, Name })), { type: "AccountModules", id: "LIST" }]
          : [{ type: "AccountModules", id: "LIST" }],
    }),
    startTrialSubscription: build.mutation<Subscription, { accountId: string }>({
      query: ({ accountId }) => ({
        url: `/v2/account-subscriptions/trial`,
        method: "POST",
        body: { account_id: accountId, kind: "trial" },
      }),
      invalidatesTags: [{ type: "Subscription", id: "LIST" }],
    }),
  }),
});

/**
 * Endpoint Hooks
 */
export const {
  useCreateAccountMutation,
  useGetAccountsQuery,
  useUpdateAccountMutation,
  useGetAccountSubscriptionsQuery,
  useLazyGetAccountSubscriptionsQuery,
  useGetAccountSubscriptionUsageQuery,
  useGetAccountUsersQuery,
  useGetAccountModulesQuery,
  useStartTrialSubscriptionMutation,
  useTransferPermissionsMutation,
} = accountSlice;

/**
 * Selectors
 */
const selectAccountsResult = accountSlice.endpoints.getAccounts.select();
const selectAccountsData = createSelector(selectAccountsResult, (accountsResult) => accountsResult.data);

export const { selectAll: selectAllAccounts, selectById: selectAccountById } = accountsAdapter.getSelectors(
  (state: Store) => selectAccountsData(state) ?? accountsInitialState
);

export const selectCurrentAccount = (state: Store): Account | null | undefined => {
  const userId = selectUserId(state);
  // TODO FND-618: selectUserAccountIdAtRandom shouldn't be necessary once users have the ability to select their account.
  const accountId = (userId && getItem(ACCOUNT_CONTEXT)?.[userId]) || selectUserAccountIdAtRandom(state);
  return accountId && selectAccountById(state, accountId);
};

// Return the currently active subscription or the first subscription.
export const selectCurrentSubscription = createSelector(
  [(state) => state, (state: Store, accountId: string) => accountId],
  (state: Store, accountId) => {
    const subscriptions = accountSlice.endpoints.getAccountSubscriptions.select(accountId)(state)?.data;
    if (!subscriptions || subscriptions?.length === 0) return null;
    const activeSubscription = subscriptions.find((subscription) => subscription.Status === SubscriptionStatus.Active);
    return activeSubscription ?? subscriptions[0];
  }
);

export const selectCurrentActiveSubscription = createSelector(
  [(state) => state, (state: Store, accountId: string) => accountId],
  (state: Store, accountId) => {
    const subscriptions = accountSlice.endpoints.getAccountSubscriptions.select(accountId)(state)?.data;
    if (!subscriptions || subscriptions?.length === 0) return null;
    return subscriptions.find((subscription) => subscription.Status === SubscriptionStatus.Active);
  }
);
